import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import {
  AbsolutePopup,
  IPopupMethods,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
} from 'core-components/library/popup'

import { isEmptyFilterValue } from '../../lib/filterValue'
import { ActiveFiltersMap, FilterConfig, FilterValue } from '../../types'
import { FilterRow } from '../filterRow/FilterRow'

import styles from './AddFilterButton.module.scss'

interface FilterPanelContentProps {
  config: FilterConfig[]
  initialFilters: ActiveFiltersMap
  onApply: (filters: ActiveFiltersMap) => void
  onClose: () => void
  onReset: () => void
  /** Max height of the filter panel */
  maxHeight?: string | number
  /** Enable search field in filter panel header when number of filters exceeds threshold */
  enableFilterSearch?: boolean
  /** Minimum number of filters to show search field. Default: 5 */
  filterSearchThreshold?: number
  /** Filter panel title. Default: "Filter" */
  filterPanelTitle?: string
  /** Filter panel search placeholder. Default: "Search filters..." */
  filterPanelSearchPlaceholder?: string
  /** Filter panel reset button text. Default: "Reset" */
  filterPanelResetText?: string
  /** Filter panel apply button text. Default: "Apply Now" */
  filterPanelApplyText?: string
}

const FilterPanelContent = memo<FilterPanelContentProps>(
  ({
    config,
    initialFilters,
    onApply,
    onReset,
    maxHeight,
    enableFilterSearch,
    filterSearchThreshold = 5,
    filterPanelTitle = 'Filter',
    filterPanelSearchPlaceholder = 'Search filters...',
    filterPanelResetText = 'Reset',
    filterPanelApplyText = 'Apply Now',
  }) => {
    const [localFilters, setLocalFilters] = useState<ActiveFiltersMap>(initialFilters)
    const [searchQuery, setSearchQuery] = useState('')

    const showSearch = enableFilterSearch && config.length >= filterSearchThreshold

    // Filter config based on search query
    const filteredConfig =
      showSearch && searchQuery.trim()
        ? config.filter((filter) =>
            filter.label.toLowerCase().includes(searchQuery.toLowerCase().trim())
          )
        : config

    // Sync when initialFilters change (when popup reopens)
    useEffect(() => {
      setLocalFilters(initialFilters)
    }, [initialFilters])

    // Get current value for a filter
    const getFilterValue = (filterId: string): FilterValue => {
      return localFilters[filterId] ?? null
    }

    // Update a single filter's value locally
    const handleFilterChange = (filterId: string, newValue: FilterValue) => {
      setLocalFilters((prev) => ({ ...prev, [filterId]: newValue }))
    }

    // Clear a single filter
    const handleClearFilter = (filterId: string) => {
      setLocalFilters((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([key]) => key !== filterId))
      )
    }

    // Reset all filters
    const handleReset = () => {
      setLocalFilters({})
      onReset()
    }

    // Apply all filters and close
    const handleApply = () => {
      // Only include filters that have a value
      const filtersWithValues = Object.entries(localFilters).reduce((acc, [id, filterValue]) => {
        if (!isEmptyFilterValue(filterValue)) {
          acc[id] = filterValue
        }
        return acc
      }, {} as ActiveFiltersMap)
      onApply(filtersWithValues)
    }

    return (
      <PopPaper
        className={styles.popPaper}
        style={
          maxHeight
            ? ({
                '--filter-panel-max-height':
                  typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
              } as React.CSSProperties)
            : undefined
        }
      >
        <div className={styles.header}>
          <span className={styles.title}>{filterPanelTitle}</span>
          {showSearch && (
            <div className={styles.searchWrapper}>
              <i className="kbase kbase-search" />
              <input
                type="text"
                className={styles.searchInput}
                placeholder={filterPanelSearchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  className={styles.clearSearchButton}
                  onClick={() => setSearchQuery('')}
                >
                  <i className="kbase kbase-xmark" />
                </button>
              )}
            </div>
          )}
        </div>

        <div className={styles.filtersList}>
          {filteredConfig.map((filterConfig) => (
            <FilterRow
              key={filterConfig.id}
              config={filterConfig}
              value={getFilterValue(filterConfig.id)}
              onChange={(newValue) => handleFilterChange(filterConfig.id, newValue)}
              onClear={() => handleClearFilter(filterConfig.id)}
            />
          ))}
        </div>

        <div className={styles.footer}>
          <Button variant="outlined" size="sm" onClick={handleReset}>
            {filterPanelResetText}
          </Button>
          <Button size="sm" onClick={handleApply}>
            {filterPanelApplyText}
          </Button>
        </div>
      </PopPaper>
    )
  }
)

FilterPanelContent.displayName = 'FilterPanelContent'

interface FilterPanelButtonProps {
  config: FilterConfig[]
  value: ActiveFiltersMap
  onChange: (filters: ActiveFiltersMap) => void
  /** Button text. Default: "Filter" */
  buttonText?: string
  /** Button color. Default: "primary" */
  buttonColor?: 'grey' | 'white' | 'primary' | 'success' | 'warning' | 'danger'
  /** Button variant. Default: "outlined" */
  buttonVariant?: 'contained' | 'outlined' | 'text'
  /** Button size. Default: "sm" */
  buttonSize?: 'sm' | 'md'
  /** Button icon class. Default: "kbase kbase-filter" */
  buttonIcon?: string
  /** Button icon position. Default: "start" */
  buttonIconPosition?: 'end' | 'start'
  /** Additional button className */
  buttonClassName?: string
  /** Additional root className */
  rootClassName?: string
  /** Additional popup className */
  popupClassName?: string
  /** Use fixed positioning for popup (for scrollable containers). Default: false */
  useFixedPosition?: boolean
  /** Horizontal start position for popup. Default: Right (popup opens to the right of button) */
  horizontalStartPosition?: TPopupHorizontal
  /** Horizontal direction for popup. Default: Right */
  horizontalDirection?: TPopupHorizontal
  /** Vertical start position for popup. Default: Top */
  verticalStartPosition?: TPopupVertical
  /** Max height of the filter panel. Can be a number (px) or string (e.g. '500px', '50vh') */
  maxHeight?: string | number
  /** Enable search field in filter panel header when number of filters exceeds threshold. Default: false */
  enableFilterSearch?: boolean
  /** Minimum number of filters to show search field. Default: 5 */
  filterSearchThreshold?: number
  /** Filter panel title. Default: "Filter" */
  filterPanelTitle?: string
  /** Filter panel search placeholder. Default: "Search filters..." */
  filterPanelSearchPlaceholder?: string
  /** Filter panel reset button text. Default: "Reset" */
  filterPanelResetText?: string
  /** Filter panel apply button text. Default: "Apply Now" */
  filterPanelApplyText?: string
}

export const FilterPanelButton = memo<FilterPanelButtonProps>(
  ({
    config,
    value,
    onChange,
    buttonText = 'Filter',
    buttonColor = 'primary',
    buttonVariant = 'outlined',
    buttonSize = 'sm',
    buttonIcon = 'kbase kbase-filter',
    buttonIconPosition = 'start',
    buttonClassName,
    rootClassName,
    popupClassName,
    useFixedPosition = false,
    horizontalStartPosition = TPopupHorizontal.Right,
    horizontalDirection = TPopupHorizontal.Right,
    verticalStartPosition = TPopupVertical.Top,
    maxHeight,
    enableFilterSearch,
    filterSearchThreshold,
    filterPanelTitle,
    filterPanelSearchPlaceholder,
    filterPanelResetText,
    filterPanelApplyText,
  }) => {
    const popupRef = useRef<IPopupMethods>(null)
    const [isOpen, setIsOpen] = useState(false)

    const handlePopupVisibility = useCallback((visible: boolean) => {
      setIsOpen(visible)
      return visible
    }, [])

    const handleApply = useCallback(
      (filters: ActiveFiltersMap) => {
        onChange(filters)
        popupRef.current?.close()
      },
      [onChange]
    )

    const handleReset = useCallback(() => {
      onChange({})
      popupRef.current?.close()
    }, [onChange])

    const handleClose = useCallback(() => {
      popupRef.current?.close()
    }, [])

    // Wrapper with context-fixed class tells AbsolutePopup to use fixed positioning
    // This is needed when filter button is inside a scrollable container
    const content = (
      <AbsolutePopup
        ref={popupRef}
        rootClassName={`${styles.root} ${rootClassName || ''}`}
        popupClassName={`${styles.popup} ${popupClassName || ''}`}
        visibilityCallback={handlePopupVisibility}
        horizontalStartPosition={horizontalStartPosition}
        horizontalDirection={horizontalDirection}
        horizontalOffset={8}
        verticalStartPosition={verticalStartPosition}
        popupContent={() =>
          isOpen ? (
            <FilterPanelContent
              config={config}
              initialFilters={value}
              onApply={handleApply}
              onClose={handleClose}
              onReset={handleReset}
              maxHeight={maxHeight}
              enableFilterSearch={enableFilterSearch}
              filterSearchThreshold={filterSearchThreshold}
              filterPanelTitle={filterPanelTitle}
              filterPanelSearchPlaceholder={filterPanelSearchPlaceholder}
              filterPanelResetText={filterPanelResetText}
              filterPanelApplyText={filterPanelApplyText}
            />
          ) : null
        }
      >
        <Button
          variant={buttonVariant}
          size={buttonSize}
          icon={buttonIcon}
          iconPosition={buttonIconPosition}
          color={buttonColor}
          className={buttonClassName ?? styles.filterButton}
        >
          <Translation text={buttonText} span />
        </Button>
      </AbsolutePopup>
    )

    if (useFixedPosition) {
      return <div className={`context-fixed ${styles.fixedWrapper}`}>{content}</div>
    }

    return content
  }
)

FilterPanelButton.displayName = 'FilterPanelButton'

// Keep old export for backwards compatibility
export const AddFilterButton = FilterPanelButton
